import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "../styles/Students.css";
import { Users, UserPlus, GraduationCap, LayoutGrid, Table2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { useCenter } from "../context/CenterContext";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const Students = () => {
  const navigate = useNavigate();
  const { activeCenterId } = useCenter();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [viewMode, setViewMode] = useState("cards");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [teacherFilter, setTeacherFilter] = useState("all");
  const [groupByTeacher, setGroupByTeacher] = useState(false);

  const [openActionsForId, setOpenActionsForId] = useState(null);

  const studentFileInputRef = useRef(null);
  const guardianFileInputRef = useRef(null);
  const popoverRef = useRef(null);

  const [uploadingStudentId, setUploadingStudentId] = useState(null);
  const [uploadingGuardianKey, setUploadingGuardianKey] = useState(null);
  const [pendingStudent, setPendingStudent] = useState(null);
  const [pendingGuardian, setPendingGuardian] = useState(null); // { student, guardian }
  const [popoverFlipLeft, setPopoverFlipLeft] = useState(false);

  useLayoutEffect(() => {
    if (openActionsForId && popoverRef.current) {
      const rect = popoverRef.current.getBoundingClientRect();
      setPopoverFlipLeft(rect.right > window.innerWidth - 16);
    } else {
      setPopoverFlipLeft(false);
    }
  }, [openActionsForId]);

  useEffect(() => {
    let isMounted = true;

    const loadStudents = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get("/api/students", {
          params: {
            centerId: activeCenterId || "",
          },
        });

        if (!isMounted) return;

        const data = res.data;
        setStudents(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!isMounted) return;
        setError(
          e?.response?.data?.error ||
          e?.message ||
          "Unable to load students"
        );
        setStudents([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadStudents();

    return () => {
      isMounted = false;
    };
  }, [activeCenterId]);


  const normalize = (v) => String(v || "").toLowerCase().trim();

  const getStudentName = (s) =>
    [s?.firstName, s?.lastName].filter(Boolean).join(" ").trim() ||
    s?.name ||
    "Unnamed Student";

  const getInitials = (s) => {
    const n = getStudentName(s);
    const parts = n.split(" ").filter(Boolean);
    const a = parts[0]?.[0] || "S";
    const b = parts[1]?.[0] || "";
    return (a + b).toUpperCase();
  };

  const getTeacher = (s) => s?.teacher || s?.classroom || "—";
  const getGradeLevel = (s) => s?.gradeLevel || s?.grade || "—";

  const getAllergies = (s) => {
    const allergyList = Array.isArray(s?.allergies)
      ? s.allergies.filter(Boolean)
      : s?.allergies
        ? String(s.allergies).split(",").map((a) => a.trim()).filter(Boolean)
        : [];

    const medicationDetails = (s?.medicationAllergies || "").trim();

    const formatted = allergyList.map((item) => {
      if (item.toLowerCase() === "medication" && medicationDetails) {
        return "Medication (" + medicationDetails + ")";
      }
      return item;
    });

    return formatted.length ? formatted.join(", ") : "—";
  };

  const getEmergency = (s) => {
    const ec = s?.emergencyContact || s?.emergency || null;
    if (!ec) {
      return {
        name: "—",
        phone: "—",
        relationship: "—",
      };
    }

    if (typeof ec === "string") {
      return {
        name: ec,
        phone: "—",
        relationship: "—",
      };
    }

    return {
      name: ec?.name || "—",
      phone: ec?.phone || ec?.contact || "—",
      relationship: ec?.relationship || "—",
    };
  };

  const getGuardians = (s) => {
    if (Array.isArray(s?.guardians) && s.guardians.length) return s.guardians;
    if (Array.isArray(s?.pickups) && s.pickups.length) return s.pickups;
    return [];
  };

  const getPrimaryGuardians = (s) => {
    return getGuardians(s).filter(
      (g, index) =>
        g?.primary ||
        g?.isPrimaryGuardian ||
        g?.primaryGuardian ||
        (!g?.authorized && !g?.isAuthorizedPickup && index === 0)
    );
  };

  const getAuthorizedPickups = (s) => {
    const explicitAuthorized = getGuardians(s).filter(
      (g) => g?.authorized || g?.authorizedPickup || g?.isAuthorizedPickup
    );

    if (explicitAuthorized.length) return explicitAuthorized;

    return getGuardians(s).filter(
      (g) => !(g?.primary || g?.isPrimaryGuardian || g?.primaryGuardian)
    );
  };

  const getGuardianSummary = (s) => {
    const primary = getPrimaryGuardians(s);
    const pickups = getAuthorizedPickups(s);

    const names = [
      ...primary.map((g) => (g?.name || "").trim()).filter(Boolean),
      ...pickups.map((g) => (g?.name || "").trim()).filter(Boolean),
    ];

    if (!names.length) return "—";

    return names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "");
  };

  const teacherOptions = useMemo(() => {
    const set = new Set(students.map((s) => String(getTeacher(s)).trim()).filter(Boolean));
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [students]);

  const filteredStudents = useMemo(() => {
    const q = normalize(search);

    return students.filter((s) => {
      const name = normalize(getStudentName(s));
      const teacher = normalize(getTeacher(s));
      const status = normalize(s?.status);
      const grade = normalize(getGradeLevel(s));
      const allergies = normalize(getAllergies(s));

      const guardiansText = normalize(
        getGuardians(s)
          .map(
            (g) =>
              `${g?.name || ""} ${g?.email || ""} ${g?.phone || ""} ${g?.relationship || ""}`.trim()
          )
          .join(" ")
      );

      const matchesSearch =
        !q ||
        name.includes(q) ||
        teacher.includes(q) ||
        status.includes(q) ||
        grade.includes(q) ||
        allergies.includes(q) ||
        guardiansText.includes(q);

      const matchesStatus = statusFilter === "all" || status === normalize(statusFilter);
      const matchesTeacher = teacherFilter === "all" || teacher === normalize(teacherFilter);

      return matchesSearch && matchesStatus && matchesTeacher;
    });
  }, [students, search, statusFilter, teacherFilter]);

  const presignUpload = async ({ file, folder }) => {
    const presignRes = await fetch(`${API_BASE}/api/generate-upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fileName: file.name,
        fileType: file.type,
        folder,
      }),
    });

    if (!presignRes.ok) {
      const t = await presignRes.text();
      throw new Error(`Presign failed (${presignRes.status}): ${t}`);
    }

    const { signedUrl, publicUrl } = await presignRes.json();
    if (!signedUrl || !publicUrl) throw new Error("Missing signedUrl/publicUrl from backend");

    const putRes = await fetch(signedUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });

    if (!putRes.ok) throw new Error(`S3 upload failed (${putRes.status})`);

    return publicUrl;
  };

  const uploadStudentPhotoFor = async (student, file) => {
    if (!student?._id || !file) return;

    setError("");
    setUploadingStudentId(student._id);

    try {
      const publicUrl = await presignUpload({ file, folder: "students" });

      const saveRes = await fetch(`${API_BASE}/api/students/${student._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photo: publicUrl }),
      });

      if (!saveRes.ok) {
        const t = await saveRes.text();
        throw new Error(`Save photo failed (${saveRes.status}): ${t}`);
      }

      const updated = await saveRes.json();
      setStudents((prev) => prev.map((s) => (s._id === updated._id ? updated : s)));
    } catch (e) {
      console.error("❌ Student photo upload failed:", e);
      setError(e?.message || "Student photo upload failed");
    } finally {
      setUploadingStudentId(null);
      setPendingStudent(null);
      if (studentFileInputRef.current) studentFileInputRef.current.value = "";
    }
  };

  const uploadGuardianPhotoFor = async (student, guardian, file) => {
    if (!student?._id || !guardian?._id || !file) return;

    setError("");
    const key = `${student._id}:${guardian._id}`;
    setUploadingGuardianKey(key);

    try {
      const publicUrl = await presignUpload({ file, folder: "guardians" });

      const saveRes = await fetch(`${API_BASE}/api/students/${student._id}/guardians/${guardian._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...guardian, photoUrl: publicUrl }),
      });

      if (!saveRes.ok) {
        const t = await saveRes.text();
        throw new Error(`Guardian save failed (${saveRes.status}): ${t}`);
      }

      const freshRes = await fetch(`${API_BASE}/api/students/${student._id}`);
      const fresh = await freshRes.json();

      setStudents((prev) => prev.map((s) => (s._id === fresh._id ? fresh : s)));
    } catch (e) {
      console.error("❌ Guardian photo upload failed:", e);
      setError(e?.message || "Guardian photo upload failed");
    } finally {
      setUploadingGuardianKey(null);
      setPendingGuardian(null);
      if (guardianFileInputRef.current) guardianFileInputRef.current.value = "";
    }
  };

  const triggerStudentUpload = (student) => {
    setPendingStudent(student);
    studentFileInputRef.current?.click();
  };

  const triggerGuardianUpload = (student, guardian) => {
    setPendingGuardian({ student, guardian });
    guardianFileInputRef.current?.click();
  };

  const goMessageGuardians = (student) => {
    navigate(`/messages/compose?studentId=${student._id}`);
  };

  const openActions = (e, studentId) => {
    e.stopPropagation();
    setOpenActionsForId((prev) => (prev === studentId ? null : studentId));
  };

  const closeActions = () => setOpenActionsForId(null);

  const actionNotWired = (label) => alert(`${label} (coming next)`);

  const Controls = () => (
    <div className="students-topbar">
      <div className="students-topbar-left">
        <input
          className="students-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search student, teacher, guardian, allergies…"
        />

        <select className="students-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Archived">Archived</option>
        </select>

        <select
          className="students-select"
          value={teacherFilter}
          onChange={(e) => setTeacherFilter(e.target.value)}
        >
          {teacherOptions.map((t) => (
            <option key={t} value={t}>
              {t === "all" ? "All Teachers" : t}
            </option>
          ))}
        </select>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
          <input
            type="checkbox"
            checked={groupByTeacher}
            onChange={(e) => setGroupByTeacher(e.target.checked)}
          />
          Group by teacher
        </label>
      </div>

      <div className="students-topbar-right">
        <div
          style={{
            display: "flex",
            border: "1px solid var(--border, #e6e6e6)",
            borderRadius: 12,
            overflow: "hidden",
            background: "var(--card-bg, #fff)",
          }}
        >
          <button
            type="button"
            onClick={() => setViewMode("cards")}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              padding: "10px 12px",
              border: "none",
              cursor: "pointer",
              background: viewMode === "cards" ? "rgba(92,77,125,0.12)" : "transparent",
              color: "var(--text, #222)",
            }}
          >
            <LayoutGrid size={16} />
            Cards
          </button>

          <button
            type="button"
            onClick={() => setViewMode("table")}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              padding: "10px 12px",
              border: "none",
              cursor: "pointer",
              background: viewMode === "table" ? "rgba(92,77,125,0.12)" : "transparent",
              color: "var(--text, #222)",
            }}
          >
            <Table2 size={16} />
            Table
          </button>
        </div>

        <button
          type="button"
          onClick={() => alert("Next step: Add Student modal/form")}
          className="student-action-btn"
          style={{ padding: "10px 12px" }}
        >
          <UserPlus size={16} />
          Add Student
        </button>
      </div>
    </div>
  );


  const ActionsPopover = ({ student }) => {
    return (
      <div
        ref={popoverRef}
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: 16,
          right: popoverFlipLeft ? "auto" : 16,
          left: popoverFlipLeft ? 16 : "auto",
          width: 280,
          zIndex: 200,
          padding: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
          overflow: "visible",
          animation: "studentsPopoverIn 0.18s ease-out",
          transformOrigin: popoverFlipLeft ? "top left" : "top right",
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Student Actions</div>

        <div style={{ display: "grid", gap: 8 }}>
          <button
            className="student-action-btn"
            type="button"
            onClick={() => {
              closeActions();
              navigate(`/students/${student._id}`);
            }}
          >
            View Full Profile
          </button>

          <button
            className="student-action-btn"
            type="button"
            onClick={() => actionNotWired("Archive / Unarchive")}
          >
            Archive / Unarchive
          </button>

          <button
            className="student-action-btn"
            type="button"
            onClick={() => actionNotWired("Mark Inactive")}
          >
            Mark Inactive
          </button>

          <button
            className="student-action-btn"
            type="button"
            onClick={() => actionNotWired("Add Guardian")}
          >
            Add Guardian
          </button>

          <button
            className="student-action-btn"
            type="button"
            onClick={() => actionNotWired("Pickup Authorization Card")}
          >
            Print Pickup Authorization Card (PDF later)
          </button>

          <button
            className="student-action-btn"
            type="button"
            onClick={() => actionNotWired("Export Student Profile")}
          >
            Export Student Profile (PDF/CSV later)
          </button>
        </div>
      </div>
    );
  };

  const StudentAvatar = ({ student, size = 46 }) => {
    const isUploading = uploadingStudentId === student._id;

    return (
      <div
        className="student-avatar"
        title="Click to upload/change student photo"
        role="button"
        tabIndex={0}
        onClick={() => triggerStudentUpload(student)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") triggerStudentUpload(student);
        }}
        style={{ width: size, height: size, cursor: "pointer", position: "relative" }}
      >
        {student?.photo ? (
          <img
            src={`${student.photo}?v=${encodeURIComponent(student.updatedAt || Date.now())}`}
            alt={getStudentName(student)}
          />
        ) : (
          getInitials(student)
        )}

        {isUploading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              background: "rgba(255,255,255,0.75)",
              fontWeight: 800,
              fontSize: 12,
            }}
          >
            Uploading…
          </div>
        )}
      </div>
    );
  };

  const GuardianAvatar = ({ student, guardian, size = 38 }) => {
    const key = `${student._id}:${guardian._id}`;
    const isUploading = uploadingGuardianKey === key;
    const initial = (guardian?.name?.trim()?.[0] || "G").toUpperCase();

    return (
      <div
        className="guardian-avatar"
        role="button"
        tabIndex={0}
        title="Click to upload guardian photo"
        onClick={() => triggerGuardianUpload(student, guardian)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") triggerGuardianUpload(student, guardian);
        }}
        style={{
          width: size,
          height: size,
          cursor: "pointer",
          position: "relative",
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
        }}
      >
        {guardian?.photoUrl ? (
          <img src={guardian.photoUrl} alt={guardian?.name || "Guardian"} />
        ) : (
          <span>{initial}</span>
        )}

        {isUploading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              background: "rgba(255,255,255,0.75)",
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            …
          </div>
        )}
      </div>
    );
  };

  const GuardianList = ({ student }) => {
    const primaryGuardians = getPrimaryGuardians(student);
    const authorizedPickups = getAuthorizedPickups(student);

    return (
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 800 }}>Primary Guardian</div>

          {primaryGuardians.length ? (
            <div style={{ display: "grid", gap: 10 }}>
              {primaryGuardians.slice(0, 1).map((g, idx) => (
                <div
                  key={g?._id || `primary-${idx}`}
                  className="guardian-card"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "38px 1fr",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <GuardianAvatar student={student} guardian={g} size={38} />

                  <div className="guardian-info" style={{ minWidth: 0 }}>
                    <strong>{g?.name || "—"}</strong>
                    <div>{g?.relationship || "—"}</div>
                    <div>
                      📞{" "}
                      {g?.phone ? (
                        <a
                          href={`tel:${g.phone}`}
                          style={{
                            color: "var(--text-secondary)",
                            textDecoration: "underline",
                          }}
                        >
                          {g.phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                    <div>
                      ✉️{" "}
                      {g?.email ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/messages/compose?studentId=${student._id}&to=${encodeURIComponent(g.email)}`
                            )
                          }
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "var(--text-secondary)",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          {g.email}
                        </button>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "var(--text-secondary)" }}>—</div>
          )}
        </div>

        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ fontWeight: 800 }}>Authorized Pickups</div>

          {authorizedPickups.length ? (
            <div style={{ display: "grid", gap: 10 }}>
              {authorizedPickups.slice(0, 2).map((g, idx) => (
                <div
                  key={g?._id || `pickup-${idx}`}
                  className="guardian-card"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "38px 1fr",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <GuardianAvatar student={student} guardian={g} size={38} />

                  <div className="guardian-info" style={{ minWidth: 0 }}>
                    <strong>{g?.name || "—"}</strong>
                    <div>{g?.relationship || "—"}</div>
                    <div>
                      📞{" "}
                      {g?.phone ? (
                        <a
                          href={`tel:${g.phone}`}
                          style={{
                            color: "var(--text-secondary)",
                            textDecoration: "underline",
                          }}
                        >
                          {g.phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>
                    <div>
                      ✉️{" "}
                      {g?.email ? (
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/messages/compose?studentId=${student._id}&to=${encodeURIComponent(g.email)}`
                            )
                          }
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "var(--text-secondary)",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          {g.email}
                        </button>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {authorizedPickups.length > 2 && (
                <div style={{ color: "var(--text-secondary)" }}>
                  +{authorizedPickups.length - 2} more pickups
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: "var(--text-secondary)" }}>—</div>
          )}
        </div>
      </div>
    );
  };

  const DirectoryCard = ({ s }) => {
    const name = getStudentName(s);
    const teacher = getTeacher(s);
    const gradeLevel = getGradeLevel(s);
    const status = String(s?.status || "—");
    const emergency = getEmergency(s);
    const allergies = getAllergies(s);

    return (
      <div key={s._id} className="card students-popover-card" style={{ position: "relative", overflow: "visible" }}>
        {openActionsForId === s._id && <ActionsPopover student={s} />}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <StudentAvatar student={s} size={46} />

            <div>
              <div style={{ fontWeight: 900 }}>{name}</div>
              <div style={{ color: "var(--text-secondary)", marginTop: 2 }}>
                {teacher} • {gradeLevel}
              </div>
            </div>
          </div>

          <span className={`badge ${normalize(status) === "active" ? "success" : "info"}`}>
            {status}
          </span>
        </div>

        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <GuardianList student={s} />

          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 800 }}>Emergency Contact</div>
            <div style={{ color: "var(--text-secondary)" }}>
              {emergency.name} •{" "}
              {emergency.phone !== "—" ? (
                <a
                  href={`tel:${emergency.phone}`}
                  style={{ color: "var(--text-secondary)", textDecoration: "underline" }}
                >
                  📞 {emergency.phone}
                </a>
              ) : (
                "📞 —"
              )}
            </div>
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ fontWeight: 800 }}>Allergies</div>
            <div style={{ color: "var(--text-secondary)" }}>{allergies}</div>
          </div>
        </div>

        <div
          className="student-actions students-action-anchor"
          style={{ marginTop: 12, alignItems: "center" }}
        >
          <button
            type="button"
            className="student-action-btn"
            onClick={(e) => openActions(e, s._id)}
          >
            View Actions
          </button>

          <button type="button" className="student-action-btn" onClick={() => goMessageGuardians(s)}>
            Message Guardians
          </button>
        </div>
      </div >
    );
  };

  const CardsView = ({ list }) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: 14,
        marginTop: 12,
      }}
    >
      {list.map((s) => (
        <DirectoryCard key={s._id} s={s} />
      ))}

      {!loading && !list.length && (
        <div className="card" style={{ gridColumn: "1 / -1" }}>
          <div style={{ fontWeight: 800 }}>No students match your filters.</div>
          <div style={{ marginTop: 6, color: "var(--text-secondary)" }}>
            Try clearing search or switching teacher/status filters.
          </div>
        </div>
      )}
    </div>
  );

  const TableView = ({ list }) => (
    <div className="card students-table-wrap" style={{ marginTop: 12, overflow: "visible" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left" }}>
            <th style={{ padding: "12px 10px", borderBottom: "1px solid var(--border, #e6e6e6)" }}>
              Student
            </th>
            <th style={{ padding: "12px 10px", borderBottom: "1px solid var(--border, #e6e6e6)" }}>
              Teacher
            </th>
            <th style={{ padding: "12px 10px", borderBottom: "1px solid var(--border, #e6e6e6)" }}>
              Status
            </th>
            <th style={{ padding: "12px 10px", borderBottom: "1px solid var(--border, #e6e6e6)" }}>
              Guardians
            </th>
            <th style={{ padding: "12px 10px", borderBottom: "1px solid var(--border, #e6e6e6)" }}>
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {list.map((s) => {
            const name = getStudentName(s);
            const teacher = getTeacher(s);
            const status = String(s?.status || "—");

            return (
              <tr key={s._id}>
                <td style={{
                  padding: "12px 10px", borderBottom: "1px solid var(--border, #eee)", position: "relative",
                  overflow: "visible",
                }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <StudentAvatar student={s} size={38} />
                    <strong>{name}</strong>
                  </div>
                </td>

                <td style={{ padding: "12px 10px", borderBottom: "1px solid var(--border, #eee)" }}>
                  {teacher}
                </td>

                <td style={{ padding: "12px 10px", borderBottom: "1px solid var(--border, #eee)" }}>
                  <span className={`badge ${normalize(status) === "active" ? "success" : "info"}`}>
                    {status}
                  </span>
                </td>

                <td style={{ padding: "12px 10px", borderBottom: "1px solid var(--border, #eee)" }}>
                  {getGuardianSummary(s)}
                </td>

                <td
                  style={{
                    padding: "12px 10px",
                    borderBottom: "1px solid var(--border, #eee)",
                    position: "relative",
                  }}
                >
                  <div
                    className="students-action-anchor"
                    style={{ display: "inline-flex", gap: 10 }}
                  >
                    <button
                      type="button"
                      className="student-action-btn"
                      onClick={(e) => openActions(e, s._id)}
                    >
                      View Actions
                    </button>

                    <button
                      type="button"
                      className="student-action-btn"
                      onClick={() => goMessageGuardians(s)}
                    >
                      Message
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}

          {!loading && !list.length && (
            <tr>
              <td colSpan={5} style={{ padding: "14px 10px" }}>
                <strong>No students match your filters.</strong>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const InsightsStrip = () => (
    <section className="card-grid" style={{ marginTop: 18 }}>
      <div className="card">
        <div className="card-icon">
          <UserPlus size={22} />
        </div>
        <h3>New Enrollments</h3>
        <p>3 pending applications</p>
        <span className="badge info">New</span>
      </div>

      <div className="card">
        <div className="card-icon">
          <GraduationCap size={22} />
        </div>
        <h3>Progress Reports</h3>
        <p>Review milestone tracking</p>
        <span className="badge info">Updated</span>
      </div>

      <div className="card">
        <div className="card-icon">
          <Users size={22} />
        </div>
        <h3>Allergies List</h3>
        <p>Print for teacher prep</p>
        <button className="student-action-btn" type="button" onClick={() => actionNotWired("Print allergies list")}>
          Print (later)
        </button>
      </div>
    </section>
  );

  const grouped = useMemo(() => {
    if (!groupByTeacher) return null;
    const map = new Map();

    filteredStudents.forEach((s) => {
      const key = String(getTeacher(s)).trim() || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });

    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredStudents, groupByTeacher]);

  useEffect(() => {
    const onDoc = () => {
      setOpenActionsForId(null);
    };

    if (openActionsForId) {
      document.addEventListener("click", onDoc);
    }

    return () => {
      document.removeEventListener("click", onDoc);
    };
  }, [openActionsForId]);

  return (
    <div className="students-page">
      <header className="header">
        <h1>
          <Users size={28} style={{ marginRight: "10px", verticalAlign: "middle" }} />
          Students
        </h1>

        <p style={{ color: "var(--text-secondary)" }}>
          Manage enrollment, guardians, student photos, allergies, and emergency contacts — fast.
        </p>

        <Controls />

        {error && <div style={{ marginTop: 10, color: "crimson" }}>{error}</div>}
      </header>

      <input
        ref={studentFileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && pendingStudent) uploadStudentPhotoFor(pendingStudent, file);
        }}
      />

      <input
        ref={guardianFileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && pendingGuardian?.student && pendingGuardian?.guardian) {
            uploadGuardianPhotoFor(pendingGuardian.student, pendingGuardian.guardian, file);
          }
        }}
      />

      <section style={{ marginTop: 18 }}>
        <div style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Student Directory</h2>
          <div style={{ marginTop: 6, color: "var(--text-secondary)" }}>
            {loading && "Loading…"}
            {!loading && !error && `${filteredStudents.length} shown`}
          </div>
        </div>

        {groupByTeacher && grouped ? (
          <div style={{ display: "grid", gap: 18 }}>
            {grouped.map(([teacher, list]) => (
              <div key={teacher} className="card" style={{ padding: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 900, fontSize: "1.05rem" }}>{teacher}</div>
                    <div style={{ color: "var(--text-secondary)", marginTop: 4 }}>{list.length} students</div>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <button
                      className="student-action-btn"
                      type="button"
                      onClick={() => navigate(`/messages/compose?teacher=${encodeURIComponent(teacher)}`)}
                    >
                      Mass Message
                    </button>
                    <button
                      className="student-action-btn"
                      type="button"
                      onClick={() => actionNotWired(`Print allergies list for ${teacher}`)}
                    >
                      Print Allergies
                    </button>
                  </div>
                </div>

                {viewMode === "cards" ? <CardsView list={list} /> : <TableView list={list} />}
              </div>
            ))}
          </div>
        ) : viewMode === "cards" ? (
          <CardsView list={filteredStudents} />
        ) : (
          <TableView list={filteredStudents} />
        )}
      </section>

      <InsightsStrip />
    </div>
  );
};

export default Students;